/**
 * TerminologyMap Interface
 *
 * Defines all customizable labels in the platform.
 * Only non-default values are stored in DB (Workspaces.settings.terminology).
 * Missing keys fall back to DEFAULT_TERMINOLOGY.
 */
export interface TerminologyMap {
  // Roles
  roleDirector: string;
  roleTeacher: string;
  roleStudent: string;
  roleStudentFormal: string;

  // Navigation / Sections
  navDashboard: string;
  navAcademicStructure: string;
  navVideoManagement: string;
  navMySubjects: string;
  navGradeManagement: string;
  navGrades: string;
  navKardex: string;
  navAchievements: string;
  navSchedule: string;
  navLMS: string;
  navForum: string;
  navSuggestions: string;
  navNotifications: string;
  navStatistics: string;
  navSettings: string;

  // Academic Terms
  termLevel: string;
  termLevelPlural: string;
  termAcademicCycle: string;
  termAcademicCyclePlural: string;
  termSubject: string;
  termSubjectPlural: string;
  termCourse: string;
  termCoursePlural: string;
}

/**
 * Default terminology values (Spanish)
 */
export const DEFAULT_TERMINOLOGY: TerminologyMap = {
  // Roles
  roleDirector: 'Director',
  roleTeacher: 'Profesor',
  roleStudent: 'Alumno',
  roleStudentFormal: 'Estudiante',

  // Navigation / Sections
  navDashboard: 'Dashboard',
  navAcademicStructure: 'Estructura Académica',
  navVideoManagement: 'Gestión de Videos',
  navMySubjects: 'Mis Materias',
  navGradeManagement: 'Gestión de Calificaciones',
  navGrades: 'Calificaciones',
  navKardex: 'Mi Kardex',
  navAchievements: 'Mis Logros',
  navSchedule: 'Horarios',
  navLMS: 'LMS',
  navForum: 'Foro',
  navSuggestions: 'Sugerencias',
  navNotifications: 'Notificaciones',
  navStatistics: 'Estadísticas',
  navSettings: 'Configuración',

  // Academic Terms
  termLevel: 'Nivel',
  termLevelPlural: 'Niveles',
  termAcademicCycle: 'Ciclo Académico',
  termAcademicCyclePlural: 'Ciclos Académicos',
  termSubject: 'Materia',
  termSubjectPlural: 'Materias',
  termCourse: 'Curso',
  termCoursePlural: 'Cursos',
};

/**
 * Category metadata for grouping in the settings UI
 */
export interface TerminologyCategoryMeta {
  id: string;
  label: string;
  description: string;
  keys: (keyof TerminologyMap)[];
}

export const TERMINOLOGY_CATEGORIES: TerminologyCategoryMeta[] = [
  {
    id: 'roles',
    label: 'Roles',
    description: 'Nombres de los roles de usuario en la plataforma',
    keys: ['roleDirector', 'roleTeacher', 'roleStudent', 'roleStudentFormal'],
  },
  {
    id: 'navigation',
    label: 'Navegación',
    description: 'Etiquetas de las secciones del menú lateral',
    keys: [
      'navDashboard',
      'navAcademicStructure',
      'navVideoManagement',
      'navMySubjects',
      'navGradeManagement',
      'navGrades',
      'navKardex',
      'navAchievements',
      'navSchedule',
      'navLMS',
      'navForum',
      'navSuggestions',
      'navNotifications',
      'navStatistics',
      'navSettings',
    ],
  },
  {
    id: 'academic',
    label: 'Términos Académicos',
    description: 'Terminología académica usada en la plataforma',
    keys: [
      'termLevel',
      'termLevelPlural',
      'termAcademicCycle',
      'termAcademicCyclePlural',
      'termSubject',
      'termSubjectPlural',
      'termCourse',
      'termCoursePlural',
    ],
  },
];

/**
 * Human-readable labels for each terminology key (used in settings UI)
 */
export const TERMINOLOGY_KEY_LABELS: Record<keyof TerminologyMap, string> = {
  roleDirector: 'Director',
  roleTeacher: 'Profesor',
  roleStudent: 'Alumno',
  roleStudentFormal: 'Estudiante (formal)',

  navDashboard: 'Dashboard',
  navAcademicStructure: 'Estructura Académica',
  navVideoManagement: 'Gestión de Videos',
  navMySubjects: 'Mis Materias',
  navGradeManagement: 'Gestión de Calificaciones',
  navGrades: 'Calificaciones',
  navKardex: 'Mi Kardex',
  navAchievements: 'Mis Logros',
  navSchedule: 'Horarios',
  navLMS: 'LMS',
  navForum: 'Foro',
  navSuggestions: 'Sugerencias',
  navNotifications: 'Notificaciones',
  navStatistics: 'Estadísticas',
  navSettings: 'Configuración',

  termLevel: 'Nivel (singular)',
  termLevelPlural: 'Nivel (plural)',
  termAcademicCycle: 'Ciclo Académico (singular)',
  termAcademicCyclePlural: 'Ciclo Académico (plural)',
  termSubject: 'Materia (singular)',
  termSubjectPlural: 'Materia (plural)',
  termCourse: 'Curso (singular)',
  termCoursePlural: 'Curso (plural)',
};

/**
 * Extract only the overridden (non-default) values from a terminology map.
 * Used to keep DB storage lean.
 */
export function getTerminologyOverrides(
  terminology: TerminologyMap
): Partial<TerminologyMap> {
  const overrides: Partial<TerminologyMap> = {};

  for (const key of Object.keys(DEFAULT_TERMINOLOGY) as (keyof TerminologyMap)[]) {
    if (terminology[key] !== DEFAULT_TERMINOLOGY[key]) {
      overrides[key] = terminology[key];
    }
  }

  return overrides;
}

/**
 * Merge partial overrides with defaults to produce a complete TerminologyMap.
 */
export function mergeWithDefaults(
  overrides: Partial<TerminologyMap> | undefined | null
): TerminologyMap {
  if (!overrides) return { ...DEFAULT_TERMINOLOGY };
  return { ...DEFAULT_TERMINOLOGY, ...overrides };
}
